<template>
  <div class="view-form">
    <div class="logo-overlay"></div>

    <!-- Registration success modal -->
    <div v-if="success" class="modal-backdrop" @click.self="dismissModal">
      <div class="modal-box" role="dialog" aria-modal="true">
        <div class="modal-icon">&#10003;</div>
        <h2 class="modal-title">{{ 'Registration Successful!' | translate }}</h2>
        <p class="modal-message">{{
          'Please check your email to set up your password. You must complete this step before you can log in.'
            | translate
        }}</p>
        <button class="btn btn-primary modal-ok-btn" @click="dismissModal">
          {{ 'OK' | translate }}
        </button>
      </div>
    </div>

    <div class="loading" v-show="!isLoaded">
      <div class="double-bounce1"></div>
      <div class="double-bounce2"></div>
    </div>
    <div
      v-show="isLoaded"
      class="container main-container main-container--login main-container--login_gray signup-form"
    >
      <div class="row">
        <div class="col">
          <div v-if="confirmMessage !== null" class="alert alert-primary" role="alert">
            {{ confirmMessage }}
          </div>
          <div v-if="error" class="alert alert-danger" role="alert">
            <span v-if="errorCode === 'auth/email-already-exists'">{{
              'The email address is already in use by another account.' | translate
            }}</span>
            <span v-else-if="errorCode === 'form/required-fields'">{{'Please fill in all required fields!' | translate }}</span>
            <span v-else>{{ errorMessage | translate }}</span>
          </div>
          <div class="card main-card">
            <div class="card-body">
              <h1 class="h1 text-center mb-4">{{ 'Create Account' | translate }}</h1>

              <form @submit.prevent="signUp">
                <div class="card-body__top d-flex justify-content-end">
                  <p>* {{ 'Field Required' | translate }}</p>
                </div>

                <div class="row">
                  <!-- First Name -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'First Name' | translate }}</label>
                      <span>*</span>
                      <input
                        type="text"
                        name="firstName"
                        class="form-control"
                        v-model="user.firstName"
                        data-vv-as="First Name"
                        v-validate="'required'"
                      />
                      <span v-show="errors.has('firstName')" class="text-danger">
                        <small>{{ 'The First Name field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>

                  <!-- Last Name -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Last Name' | translate }}</label>
                      <span>*</span>
                      <input
                        type="text"
                        name="lastName"
                        class="form-control"
                        v-model="user.lastName"
                        data-vv-as="Last Name"
                        v-validate="'required'"
                      />
                      <span v-show="errors.has('lastName')" class="text-danger">
                        <small>{{ 'The Last Name field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>
                  <!-- Job Title -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Title' | translate }}</label>
                      <span>*</span>
                      <input
                        type="text"
                        name="title"
                        class="form-control"
                        v-model="user.title"
                        data-vv-as="Job Title"
                        v-validate="'required'"
                      />
                      <span v-show="errors.has('title')" class="text-danger">
                        <small>{{ 'The Title field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>
                </div>
                <!-- end row -->

                <div class="row">
                  <!-- Email -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Email' | translate }}</label>
                      <span>*</span>
                      <input
                        type="email"
                        name="email"
                        class="form-control"
                        v-model="user.email"
                        data-vv-as="Email"
                        v-validate="'required|email'"
                      />
                      <span v-show="errors.has('email')" class="text-danger">
                        <small>{{ 'The Email field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>

                  <!-- Country -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Country' | translate }}</label>
                      <span>*</span>
                      <select
                        name="country"
                        class="form-control"
                        v-model="user.countryCode"
                        data-vv-as="Country"
                        v-validate="'required'"
                      >
                        <option :value="null" disabled>{{ 'Select' | translate }}</option>
                        <option
                          v-for="(country, index) in $store.state.misc.countriesPrimary"
                          :key="index"
                          :value="index"
                        >
                          {{ country | translate }}
                        </option>
                        <option disabled>------------------</option>
                        <option
                          v-for="(country, index) in $store.state.misc.countries"
                          :key="index"
                          :value="index"
                        >
                          {{ country | translate }}
                        </option>
                      </select>
                      <span v-show="errors.has('country')" class="text-danger">
                        <small>{{ 'The Country field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>

                  <!-- Phone -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Phone' | translate }}</label>
                      <span>*</span>
                      <input
                        type="tel"
                        name="phone"
                        class="form-control"
                        v-model="user.phone"
                        data-vv-as="Phone"
                        v-validate="'required'"
                      />
                      <span v-show="errors.has('phone')" class="text-danger">
                        <small>{{ 'The Phone field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <!-- City -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'City' | translate }}</label>
                      <span>*</span>
                      <input
                        type="text"
                        name="city"
                        class="form-control"
                        v-model="user.city"
                        data-vv-as="City"
                        v-validate="'required'"
                      />
                      <span v-show="errors.has('city')" class="text-danger">
                        <small>{{ 'The City field is required.' | translate }}</small>
                      </span>
                    </div>
                  </div>

                  <!-- State/Province -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'State/Province' | translate }}</label>
                      <select
                        v-if="misc.states[user.countryCode]"
                        class="form-control"
                        v-model="user.stateProvince"
                      >
                        <option :value="null" disabled>{{ 'Select' | translate }}</option>
                        <template
                          v-if="
                            misc.states[user.countryCode].primary &&
                            Object.keys(misc.states[user.countryCode].primary).length
                          "
                        >
                          <option
                            v-for="(state, abbr) in misc.states[user.countryCode].primary"
                            :key="state"
                            :value="abbr"
                          >
                            {{ state }}
                          </option>
                          <option disabled>------------------</option>
                        </template>
                        <option
                          v-for="(state, abbr) in misc.states[user.countryCode].regular"
                          :key="state"
                          :value="abbr"
                        >
                          {{ state }}
                        </option>
                      </select>
                      <input v-else v-model="user.stateProvince" type="text" class="form-control" />
                    </div>
                  </div>
                  <!-- Postal Code -->
                  <div class="col-24 col-md-8">
                    <div class="form-group">
                      <label>{{ 'Postal Code' | translate }}</label>
                      <input
                        v-model="user.postalCode"
                        type="text"
                        name="postalCode"
                        class="form-control"
                        data-vv-as="Postal Code"
                      />
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-16 d-flex align-items-center mb-2 mb-md-0">
                    <label class="label-checkbox">
                      <input
                        type="checkbox"
                        id="agree"
                        name="terms"
                        data-vv-as="Terms"
                        v-validate="'required'"
                      />
                      <span class="checkbox"></span>
                    </label>
                    <label class="label--font-thin label--login-checkbox mb-0" for="agree">
                      {{ 'I agree to the' | translate }}
                      <router-link :to="'/terms-of-use'" target="_blank" class="underline-bold">{{
                        'Terms of Use' | translate
                      }}</router-link>
                      {{ 'and' | translate }}
                      <a :href="getPrivacyPolicyUrl()" target="_blank" class="underline-bold">{{
                        'Privacy Policy' | translate
                      }}</a>
                    </label>
                  </div>
                  <div class="col-md-8 d-flex justify-content-end">
                    <button type="submit" class="btn btn-primary btn-full--sm" :disabled="loader">
                      {{ 'Create Account' | translate }}
                      <span v-if="loader" class="loader ml-1"></span>
                    </button>
                  </div>
                  <span v-show="errors.has('terms')" class="text-danger">
                    <small>{{ errors.first('terms') | translate }}</small>
                  </span>
                </div>

                <div class="text-center mt-3">
                  <p>
                    {{ 'Already have an account?' | translate }}
                    <router-link to="/login" class="btn-link">
                      {{ 'Login' | translate }}
                    </router-link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { GET_SIGNUP_INTRO } from '../store/types';

export default {
  name: 'SignUp',
  data() {
    return {
      isLoaded: true,
      loader: false,
      success: false,
      error: false,
      errorCode: '',
      errorMessage: '',
      confirmMessage: null,
      user: {
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        phone: '',
        countryCode: null,
        city: '',
        stateProvince: null,
        postalCode: '',
      },
    };
  },
  computed: {
    ...mapState({
      misc: (state) => state.misc,
    }),
  },
  methods: {
    ...mapActions('users', [GET_SIGNUP_INTRO]),

    dismissModal() {
      this.$router.push('/login');
    },

    generateErrorMsg({ errors, message = '' }) {
      return errors ? Object.values(errors).join(', ') : message;
    },

    getPrivacyPolicyUrl() {
      const languageMap = {
        en: 'https://www.zinpro.com/privacy-policy/',
        es: 'https://www.zinpro.com/es_mx/privacy-policy/',
        pt: 'https://www.zinpro.com/pt/privacy-policy/',
        fr: 'https://www.zinpro.com/fr/privacy-policy/',
        pl: 'https://www.zinpro.com/pl/privacy-policy/',
        ru: 'https://zinpro.pro/politika-konfidencialnosti/',
      };

      const userLang = this.$i18n.locale() || 'en';
      return languageMap[userLang] || languageMap.en;
    },

    async signUp() {
      this.error = false;
      this.success = false;
      this.loader = true;

      const allFieldsValid = await this.$validator.validateAll();

      if (!allFieldsValid) {
        this.error = true;
        this.errorCode = 'form/required-fields';
        this.loader = false;
        setTimeout(() => {
          this.errorCode = '';
          this.error = false;
          this.errorMessage = '';
        }, 4000);
        return;
      }

      try {
        // Get current language from store or default to 'en'
        const currentLang = this.$i18n.locale() || 'en';

        // Prepare user data with language
        const userData = {
          ...this.user,
          lang: currentLang,
        };

        await this[GET_SIGNUP_INTRO](userData);
        this.success = true;
        this.loader = false;
      } catch (err) {
        console.error('Signup error:', err);
        this.error = true;
        this.loader = false;
        this.errorCode = err.code || 'auth/something-wrong';
        this.errorMessage = this.generateErrorMsg(err);

        setTimeout(() => {
          this.error = false;
          this.errorCode = '';
          this.errorMessage = '';
        }, 5000);
      }
    },
  },
  created() {
    // Load misc data if not already loaded
    if (!this.$store.state.misc.countries) {
      this.$store.dispatch('misc/GET_MISC_DATA');
    }
  },
};
</script>

<style scoped>
.main-card {
  max-width: 800px;
  margin: 0 auto;
}

.btn-full--sm {
  width: 100%;
  max-width: 200px;
}

.btn-link {
  color: #007bff;
  text-decoration: none;
}

.btn-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.required {
  color: #dc3545;
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Override the login form styling to match VerifyAccount layout */
.form-group {
  display: block !important;
}

.form-group .login__label {
  width: 100% !important;
  display: block !important;
  margin-bottom: 0.5rem !important;
  color: #000 !important;
  font-size: 16px !important;
  font-weight: 500 !important;
}

.form-group input.form-control,
.form-group select.form-control {
  width: 100% !important;
  margin-top: 0.25rem !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-card {
    margin: 0 15px;
  }

  .col-lg-12 {
    margin-bottom: 0.5rem;
  }
}

/* Ensure proper spacing between form sections */
.row {
  margin-bottom: 0;
}

.col-lg-12,
.col-md-8,
.col-24 {
  padding-left: 15px;
  padding-right: 15px;
}

.main-container--login {
  max-width: 800px;
}
.main-container {
  padding-top: 0;
}

/* Registration success modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-box {
  background: #fff;
  border-radius: 8px;
  padding: 40px 48px;
  max-width: 480px;
  width: 90%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
}

.modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #28a745;
  color: #fff;
  font-size: 32px;
  line-height: 64px;
  margin: 0 auto 20px;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: #222;
}

.modal-message {
  font-size: 1rem;
  color: #555;
  margin-bottom: 28px;
  line-height: 1.5;
}

.modal-ok-btn {
  min-width: 120px;
  font-size: 1rem;
  padding: 10px 32px;
}

@media screen and (max-width: 768px) {
  .card-body {
    padding: 0;
  }

  .card.main-card {
    padding: 0;
  }
  .col-sm-24 {
    -webkit-box-flex: 0;
    -ms-flex: 0 0 100%;
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>